import { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  InputAdornment,
  Button,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TableSortLabel,
  TablePagination
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { FileText, RefreshCw, Search } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../services/api';

export default function SpokiTemplatesPage() {
  const STATUS_OPTIONS = [
    { value: 'draft', label: 'Bozza', color: 'default' },
    { value: 'requested', label: 'Richiesto', color: 'warning' },
    { value: 'request_failed', label: 'Richiesta fallita', color: 'error' },
    { value: 'in_review', label: 'In revisione', color: 'warning' },
    { value: 'approved', label: 'Approvato', color: 'success' },
    { value: 'rejected', label: 'Rifiutato', color: 'error' },
    { value: 'submitted', label: 'Inviato', color: 'warning' },
    { value: 'paused', label: 'In pausa', color: 'warning' }
  ];
  const normalizeStatusValue = (s) => {
    if (!s) return '';
    const n = String(s).trim().toLowerCase();
    switch (n) {
      case 'bozza': return 'draft';
      case 'richiesto': return 'requested';
      case 'richiesta fallita': return 'request_failed';
      case 'in revisione': return 'in_review';
      case 'approvato': return 'approved';
      case 'rifiutato': return 'rejected';
      case 'inviato': return 'submitted';
      case 'in pausa': return 'paused';
      default: return n;
    }
  };
  const getStatusOption = (value) => {
    const v = normalizeStatusValue(value);
    return STATUS_OPTIONS.find((o) => o.value === v) || null;
  };
  const deriveTemplateStatus = (meta = {}, templateStatus = null) => {
    const normalize = (value) => (value ? normalizeStatusValue(value) : '');
    const directStatus = normalize(templateStatus);
    if (directStatus) return directStatus;

    const details = meta.details || meta || {};
    const detailStatus = normalize(details.status || meta.status);
    if (detailStatus) return detailStatus;

    const rawLocalizations = [
      ...(Array.isArray(details.templatelocalization_set) ? details.templatelocalization_set : []),
      ...(Array.isArray(meta.templatelocalization_set) ? meta.templatelocalization_set : []),
      ...(Array.isArray(details.localizations) ? details.localizations : []),
      ...(Array.isArray(meta.localizations) ? meta.localizations : [])
    ];
    const uniqueStatuses = Array.from(
      new Set(rawLocalizations.map((item) => normalize(item?.status)).filter(Boolean))
    );

    if (uniqueStatuses.length === 1) return uniqueStatuses[0];
    if (uniqueStatuses.includes('approved')) return 'approved';
    if (typeof meta.is_approved === 'boolean' && meta.is_approved) return 'approved';
    return uniqueStatuses[0] || '';
  };
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [locMap, setLocMap] = useState({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [orderBy, setOrderBy] = useState('updated_at');
  const [orderDirection, setOrderDirection] = useState('desc');
  const [filters, setFilters] = useState({
    name: '',
    status: 'all',
    language: ''
  });
  const languageLabel = (code) => {
    if (!code) return '';
    const c = String(code).toLowerCase();
    if (c.startsWith('it')) return 'Italiano';
    if (c.startsWith('en')) return 'Inglese';
    if (c.startsWith('es')) return 'Spagnolo';
    if (c.startsWith('de')) return 'Tedesco';
    if (c.startsWith('fr')) return 'Francese';
    if (c.startsWith('pt')) {
      if (c.includes('br')) return 'Portoghese (BR)';
      return 'Portoghese';
    }
    return code.toUpperCase();
  };
  const formatDateShort = (value) => {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yy = String(d.getFullYear()).slice(-2);
    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${dd}/${mm}/${yy}, ${hh}:${min}`;
  };

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        orderBy,
        orderDirection
      };
      if (filters.name) params.name = filters.name;
      if (filters.status && filters.status !== 'all') params.status = filters.status;
      if (filters.language) params.language = filters.language;
      const res = await api.get('/spoki/templates', { params });
      const result = res.data || {};
      const items = result.data || [];
      setTemplates(items);
      setTotal(result.meta?.total || items.length);
      const map = {};
      for (const t of items) {
        let meta = t.metadata || {};
        if (typeof meta === 'string') {
          try {
            meta = JSON.parse(meta);
          } catch {
            meta = {};
          }
        }
        const details = meta.details || meta || {};
        const rawLocalizations = [
          ...(Array.isArray(details.templatelocalization_set) ? details.templatelocalization_set : []),
          ...(Array.isArray(meta.templatelocalization_set) ? meta.templatelocalization_set : []),
          ...(Array.isArray(details.localizations) ? details.localizations : []),
          ...(Array.isArray(meta.localizations) ? meta.localizations : [])
        ];
        const languages = [];
        let previewIt = '';
        let bodySource =
          details.body_template_component ||
          details.body_template ||
          details.body ||
          null;
        if (!bodySource && meta.body_template_component) {
          bodySource = meta.body_template_component;
        }
        if (typeof bodySource === 'string') {
          previewIt = bodySource;
        } else if (bodySource && typeof bodySource === 'object') {
          previewIt = bodySource.text || '';
        }
        if (!previewIt && rawLocalizations.length) {
          const italianLoc =
            rawLocalizations.find((loc) => String(loc.language || loc.language_code || '').toLowerCase().startsWith('it')) ||
            rawLocalizations[0];
          const italianBody =
            italianLoc?.body_text ||
            (typeof italianLoc?.body === 'string' ? italianLoc.body : italianLoc?.body?.text || null) ||
            italianLoc?.body_template_component?.text ||
            '';
          previewIt = italianBody;
        }
        rawLocalizations.forEach((loc) => {
          const languageCode = loc.language || loc.language_code || loc.locale || '';
          if (languageCode && !languages.includes(languageCode)) {
            languages.push(languageCode);
          }
        });
        const statusFromDetail =
          deriveTemplateStatus(meta, t.status) || null;
        const updatedAt =
          meta.updated_at ||
          t.updated_at ||
          null;
        const folders =
          meta.folders ||
          meta.folder ||
          [];
        map[t.id] = {
          languages,
          previewIt,
          updatedAt,
          folders,
          status: statusFromDetail
        };
      }
      setLocMap(map);
    } catch (error) {
      toast.error('Errore nel caricamento dei template Spoki');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTemplates();
  }, [page, rowsPerPage, orderBy, orderDirection]);

  const handleSync = async () => {
    try {
      setSyncing(true);
      const res = await api.post('/spoki/templates/sync');
      const count = res.data && typeof res.data.count === 'number' ? res.data.count : null;
      if (count !== null) {
        toast.success(`Sincronizzati ${count} template Spoki`);
      } else {
        toast.success('Sincronizzazione template Spoki completata');
      }
      loadTemplates();
    } catch (error) {
      toast.error('Errore durante la sincronizzazione template Spoki');
    } finally {
      setSyncing(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value
    }));
    setPage(0);
  };

  useEffect(() => {
    const t = setTimeout(() => {
      loadTemplates();
    }, 300);
    return () => clearTimeout(t);
  }, [filters]);
  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && orderDirection === 'asc';
    setOrderDirection(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#eef2ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <FileText size={20} color="#4f46e5" />
          </Box>
          <Box>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary' }}>
              Template Spoki
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Sincronizza e consulta i template approvati
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          onClick={handleSync}
          disabled={syncing}
          startIcon={syncing ? <CircularProgress size={20} color="inherit" /> : <RefreshCw size={20} />}
        >
          {syncing ? 'Sincronizzazione...' : 'Sincronizza'}
        </Button>
      </Box>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <StyledCard>
            <CardContent>
              <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <TextField
                  fullWidth
                  size="small"
                  value={filters.name}
                  onChange={(e) => handleFilterChange('name', e.target.value)}
                  placeholder="Cerca per nome..."
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search size={20} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ maxWidth: 300 }}
                />
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel id="spoki-template-status-label">Stato</InputLabel>
                  <Select
                    labelId="spoki-template-status-label"
                    label="Stato"
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                  >
                    <MenuItem value="all">Tutti</MenuItem>
                    {STATUS_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Lingua"
                  value={filters.language}
                  onChange={(e) => handleFilterChange('language', e.target.value)}
                  size="small"
                  placeholder="it, en, es"
                  sx={{ minWidth: 180 }}
                />
              </Box>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'background.neutral' }}>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'name'}
                        direction={orderBy === 'name' ? orderDirection : 'asc'}
                        onClick={() => handleRequestSort('name')}
                      >
                        Nome
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Stato</TableCell>
                    <TableCell>Categoria</TableCell>
                    <TableCell>Anteprima in Italiano</TableCell>
                    <TableCell>Lingue</TableCell>
                    <TableCell>Cartelle</TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'updated_at'}
                        direction={orderBy === 'updated_at' ? orderDirection : 'asc'}
                        onClick={() => handleRequestSort('updated_at')}
                      >
                        Ultimo aggiornamento
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading && (
                    <TableRow>
                      <TableCell colSpan={7}>
                        <Typography variant="body2" color="text.secondary">
                          Caricamento template...
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {templates.map((t) => (
                    <TableRow key={t.id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="body2" sx={{ textDecoration: 'underline', cursor: 'pointer' }}>
                            {t.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ID: {t.spoki_template_id}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const effectiveStatus = locMap[t.id]?.status || t.status || '';
                          if (!effectiveStatus) {
                            return '-';
                          }
                          const info = getStatusOption(effectiveStatus);
                          if (info) {
                            return (
                              <Chip
                                size="small"
                                color={info.color}
                                label={info.label}
                              />
                            );
                          }
                          return (
                            <Chip
                              size="small"
                              variant="outlined"
                              label={String(effectiveStatus)}
                            />
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        {t.category ? (
                          <Chip size="small" variant="outlined" label={t.category} />
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 360 }}>
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {(locMap[t.id]?.previewIt || '').trim() || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {(locMap[t.id]?.languages || []).length ? (
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {locMap[t.id].languages.map((lng) => (
                              <Chip
                                key={`${t.id}-${lng}`}
                                size="small"
                                label={languageLabel(lng)}
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        ) : (
                          t.default_language || '-'
                        )}
                      </TableCell>
                      <TableCell>
                        {Array.isArray(locMap[t.id]?.folders) && locMap[t.id].folders.length ? (
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {locMap[t.id].folders.map((f, idx) => (
                              <Chip key={`${t.id}-f-${idx}`} size="small" variant="outlined" label={String(f)} />
                            ))}
                          </Box>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        {locMap[t.id]?.updatedAt
                          ? formatDateShort(locMap[t.id].updatedAt)
                          : (t.updated_at ? formatDateShort(t.updated_at) : '-')}
                      </TableCell>
                    </TableRow>
                  ))}
                  {templates.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Typography variant="body2" color="text.secondary">
                          Nessun template trovato
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={total}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Righe per pagina:"
              />
            </CardContent>
          </StyledCard>
        </Grid>
      </Grid>
    </Box>
  );
}

const StyledCard = styled(Card)({
  boxShadow:
    '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden'
});
